/**
 * Client-side mirror of the BE's template rules
 * (discovr-backend internal/outreach/templates/validate.go + render/merge.go).
 * It exists so the form can name the problem before the round trip — the
 * server stays authoritative, and its INVALID_STEPS / UNKNOWN_MERGE_TAG
 * responses are still surfaced inline when they arrive.
 */
import type { OutreachTemplateStep } from '@discovr/types';
import { BODY_MAX, SUBJECT_MAX, countChars } from './draftCaps';

/** render.KnownTags, in the BE's order. */
export const KNOWN_MERGE_TAGS = [
  'first_name',
  'brand',
  'campaign',
  'sender',
  'followers',
  'last_rate',
  'year',
  'creator_name',
] as const;

/** render.HookTag: filled by the AI at send time, so it is not an ordinary merge tag and stays out of KNOWN_MERGE_TAGS. */
export const AI_HOOK_TAG = 'ai_hook';
export const AI_HOOK_IN_SUBJECT_COPY = '{ai_hook} cannot be used in a subject. Put it in the email body.';
export const AI_HOOK_REPEATED_COPY = '{ai_hook} can appear only once in a step.';

export type KnownMergeTag = (typeof KNOWN_MERGE_TAGS)[number];

/** Mirrors the BE's maxSteps (templates/validate.go): the first email and 2 follow-ups. */
export const MAX_STEPS = 3;
export const MAX_FOLLOW_UPS = MAX_STEPS - 1;
export const FOLLOW_UP_CAP_NOTE = `Up to ${MAX_FOLLOW_UPS} follow-ups`;
export const TEMPLATE_STEP_LIMIT_COPY = `A template is the first email and up to ${MAX_FOLLOW_UPS} follow-ups.`;
const MAX_NAME_LENGTH = 120;
const MAX_SUBJECT_LENGTH = 200;
const MAX_BODY_LENGTH = 10_000;
/** The BE limit is in UTF-8 bytes; a UTF-16 length never exceeds it, so this also works as a `maxLength`. */
export const MAX_SIGNATURE_LENGTH = 500;
export const SIGNATURE_TOO_LONG = 'Signature must be 500 bytes or fewer — accented letters and symbols count as more than one.';

// Go's unicode.IsSpace set: JS trim() also strips U+FEFF but not U+0085.
const GO_TRIM = /^[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g;

/** Bytes as the BE counts them: after CRLF folding and Go's strings.TrimSpace. */
export function signatureBytes(signature: string): number {
  return new TextEncoder().encode(signature.replace(/\r\n/g, '\n').replace(GO_TRIM, '')).length;
}

/** A step as the form holds it — `delayDays` stays a string so the field can
 *  be empty mid-edit without collapsing to 0. */
export interface TemplateStepDraft {
  stepNo: number;
  delayDays: string;
  subject: string;
  body: string;
  /** The editor's formatting for `body`; '' when written plain. */
  bodyHtml: string;
  /** "HH:MM" time of day, follow-ups only; '' = none. */
  sendTime: string;
  /** AI wrote or helped write this step. Travels with the step through every edit, insert and save. */
  aiAssisted: boolean;
}

const SEND_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const tagPattern = () => /\{([a-z_]+)\}/g;

const isKnown = (tag: string): boolean => (KNOWN_MERGE_TAGS as readonly string[]).includes(tag) || tag === AI_HOOK_TAG;

/** Every `{tag}` in `text` that the BE would reject, deduped in first-occurrence order. */
export function unknownMergeTags(text: string): string[] {
  const out: string[] = [];
  for (const match of text.matchAll(tagPattern())) {
    const tag = match[1];
    if (!tag || isKnown(tag) || out.includes(tag)) continue;
    out.push(tag);
  }
  return out;
}

const formatTags = (tags: readonly string[]): string => tags.map((t) => `{${t}}`).join(', ');

/**
 * Returns every reason the draft would be rejected. Unlike the BE — which
 * returns the FIRST failure — this collects them all, so a form with both a
 * missing subject and a typo'd merge tag shows both at once.
 */
export function validateTemplateDraft(name: string, steps: TemplateStepDraft[], signature = ''): string[] {
  const errors: string[] = [];

  const trimmedName = name.trim();
  if (!trimmedName) errors.push('Template name is required.');
  else if (trimmedName.length > MAX_NAME_LENGTH) errors.push('Template name must be 120 characters or fewer.');

  if (steps.length === 0) errors.push('At least one step is required.');
  if (steps.length > MAX_STEPS) errors.push(TEMPLATE_STEP_LIMIT_COPY);

  const unknown: string[] = [];
  steps.forEach((step, index) => {
    const stepNo = index + 1;
    if (!step.body.trim()) errors.push(`Step ${stepNo} needs a body.`);
    else if (step.body.length > MAX_BODY_LENGTH) errors.push(`Step ${stepNo}'s body is too long.`);
    if (step.subject.length > MAX_SUBJECT_LENGTH) errors.push(`Step ${stepNo}'s subject is too long.`);

    if (stepNo === 1) {
      if (!step.subject.trim()) errors.push('Step 1 needs a subject — it is the first touch.');
      else if (countChars(step.subject) > SUBJECT_MAX) {
        errors.push(`Step 1's subject is over ${SUBJECT_MAX} characters. Shorten it to save.`);
      }
    } else {
      if (countChars(step.body) > BODY_MAX) {
        errors.push(`Step ${stepNo}'s body is over ${BODY_MAX.toLocaleString('en-US')} characters. A follow-up is written from this step and checked before it goes out, so shorten it to save.`);
      }
      const delay = Number(step.delayDays);
      if (!step.delayDays.trim() || Number.isNaN(delay) || delay <= 0) {
        errors.push(`Step ${stepNo} needs a delay of more than 0 days.`);
      }
      if (step.sendTime && !SEND_TIME.test(step.sendTime)) {
        errors.push(`Step ${stepNo} send time must be a time of day like 09:30.`);
      }
    }

    const hook = `{${AI_HOOK_TAG}}`;
    if (step.subject.includes(hook)) errors.push(`Step ${stepNo}: ${AI_HOOK_IN_SUBJECT_COPY}`);
    if (step.body.split(hook).length - 1 > 1) errors.push(`Step ${stepNo}: ${AI_HOOK_REPEATED_COPY}`);

    for (const tag of [...unknownMergeTags(step.subject), ...unknownMergeTags(step.body)]) {
      if (!unknown.includes(tag)) unknown.push(tag);
    }
  });

  if (unknown.length > 0) {
    errors.push(
      `Unknown merge tag${unknown.length > 1 ? 's' : ''}: ${formatTags(unknown)}. Known tags: ${formatTags(KNOWN_MERGE_TAGS)}.`,
    );
  }

  if (signatureBytes(signature) > MAX_SIGNATURE_LENGTH) errors.push(SIGNATURE_TOO_LONG);

  return errors;
}

/** Drafts → the wire shape: step 1 never carries a delay, later steps always do. */
export function toTemplateSteps(drafts: TemplateStepDraft[]): OutreachTemplateStep[] {
  return drafts.map((draft, index) => {
    const stepNo = index + 1;
    const bodyHtml = draft.bodyHtml ? draft.bodyHtml : undefined;
    const aiAssisted = draft.aiAssisted;
    if (stepNo === 1) {
      return { stepNo, subject: draft.subject.trim() ? draft.subject : undefined, body: draft.body, bodyHtml, aiAssisted };
    }
    // A follow-up threads under the first email, so it carries no subject.
    return {
      stepNo,
      delayDays: Number(draft.delayDays),
      body: draft.body,
      bodyHtml,
      // A send time is only valid on a whole-day wait (the server refuses it otherwise).
      ...(draft.sendTime && Number.isInteger(Number(draft.delayDays)) ? { sendTime: draft.sendTime } : {}),
      aiAssisted,
    };
  });
}

/** Wire steps → drafts, for edit and fork. */
export function toStepDrafts(steps: readonly OutreachTemplateStep[]): TemplateStepDraft[] {
  return steps.filter((step) => !step.manual).map((step, index) => ({
    stepNo: index + 1,
    delayDays: step.delayDays == null ? '' : String(step.delayDays),
    subject: index === 0 ? (step.subject ?? '') : '',
    body: step.body,
    bodyHtml: step.bodyHtml ?? '',
    sendTime: index === 0 ? '' : (step.sendTime ?? ''),
    aiAssisted: step.aiAssisted ?? false,
  }));
}

export const DEFAULT_FOLLOWUP_DELAY_DAYS = '3';

export const FIRST_STEP_LINK_NOTE = "Step 1 is the first email and can't contain a link. Add links in a later step.";

export function emptyFirstStep(): TemplateStepDraft {
  return { stepNo: 1, delayDays: '', subject: '', body: '', bodyHtml: '', sendTime: '', aiAssisted: false };
}

export function emptyFollowUpStep(stepNo: number): TemplateStepDraft {
  return { stepNo, delayDays: DEFAULT_FOLLOWUP_DELAY_DAYS, subject: '', body: '', bodyHtml: '', sendTime: '', aiAssisted: false };
}

export const FALLBACK_TEMPLATE_NAME = 'New template';

/** The name a new template starts with: its first email's subject, until the admin types a name of their own. */
export function defaultTemplateName(firstSubject: string): string {
  const trimmed = firstSubject.trim();
  return trimmed ? trimmed.slice(0, MAX_NAME_LENGTH) : FALLBACK_TEMPLATE_NAME;
}
